import { supabase } from '../client';

/**
 * Per-store public-page overrides.
 *
 * Backed by `core.public_page_templates` + `core.store_public_page_overrides`
 * and three SECURITY DEFINER RPCs shipped in
 * `supabase/migrations/20260622010500_calendar_store_public_page_templates.sql`:
 *
 *   - core.rpc_list_public_page_catalog(p_store_id)
 *   - core.rpc_upsert_store_public_page_override(...)
 *   - core.rpc_reset_store_public_page_override(...)
 *
 * The list RPC returns one row per platform public page with the
 * per-store override merged in. Today the catalog ships three keys:
 *
 *   - `calendar_public_default`    (initial /c/<token> view)
 *   - `calendar_public_confirmed`  (post-confirm view)
 *   - `calendar_public_cancelled`  (post-cancel + already-cancelled view)
 *
 * Each row exposes the platform body, the optional store override, and
 * a `has_override` flag that drives the "Customized" badge in the UI.
 */

export type PublicPageKey =
  | 'calendar_public_default'
  | 'calendar_public_confirmed'
  | 'calendar_public_cancelled';

export interface PublicPageCatalogEntry {
  key: PublicPageKey;
  name: string;
  description: string | null;
  variables: unknown;
  sample_data: unknown;
  platform_body_html: string;
  override_body_html: string | null;
  override_enabled: boolean;
  has_override: boolean;
  updated_at: string | null;
}

export async function listPublicPageCatalog(
  storeId: string,
): Promise<PublicPageCatalogEntry[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_list_public_page_catalog', { p_store_id: storeId });
  if (error) throw error;
  return (data ?? []) as PublicPageCatalogEntry[];
}

export interface UpsertStorePublicPageOverrideInput {
  storeId: string;
  pageKey: PublicPageKey;
  /**
   * Pass `null` to leave the existing override untouched.
   * Pass `''` to revert that field to platform default.
   * Pass any non-empty string to set the override.
   */
  bodyHtml?: string | null;
  enabled?: boolean | null;
}

export async function upsertStorePublicPageOverride(
  input: UpsertStorePublicPageOverrideInput,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_upsert_store_public_page_override', {
      p_store_id: input.storeId,
      p_page_key: input.pageKey,
      p_body_html: input.bodyHtml ?? null,
      p_enabled: input.enabled ?? null,
    });
  if (error) throw error;
}

export async function resetStorePublicPageOverride(
  storeId: string,
  pageKey: PublicPageKey,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_reset_store_public_page_override', {
      p_store_id: storeId,
      p_page_key: pageKey,
    });
  if (error) throw error;
}
